import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTheme } from '@mui/material/styles';

import { cleanStr } from '../utils/cleanStr';
import {
  FlamethrowerTrackingApplications,
  FlamethrowerTrackingEventTypes,
  ModuleName,
  Permission,
  Routes,
  SolveConfigTrackingEventTypes,
  Tabs,
} from '../utils/enums';
import { sendTrackingEvents } from '../utils/sendTrackingEvent';
import {
  constructTimeRangeQueryParamsRevamped,
  last30DaysTimeRange,
} from '../utils/timeRangeHelpers';
import { useIsInAssist } from './useIsInAssist';
import { getMacroControlsData } from 'src/actions/macroControls';
import {
  setMacroControlsViewMode,
  setPageNumber,
  setSolveView,
  setTimeFrameFilter,
  setTimeRange,
} from 'src/actions/pageSettings/pageSettings';
import { sendTrackingEvent } from 'src/actions/search';
import {
  getSolveChannelsAPI,
  setChannel,
  setFilterType,
  setIsSolveWorkflows,
} from 'src/actions/solveData';
import { channelsList } from 'src/actions/workflow-builder/builder-landing-page-helpers/builderLandingPageHelpers';
import {
  getWorkflowBuilderLandingData,
  setIsOverlayVisible,
  setIsPreviewLoaded,
  setWorkflowBuilderChannel,
} from 'src/actions/workflow-builder/workflowBuilderActions';
import { mutationObserverCallback } from 'src/components/app/layout/mutationObserverCallback';
import {
  TrackingEventScope,
  TrackingEventType,
} from 'src/components/app/types';
import { getFilterDict } from 'src/pages/solve-analytics/filterDict';
import { BuilderView } from 'src/pages/workflow-builder-edit/types';
import { selectCustomizableActionId } from 'src/reducers/actionBuilderReducer/actionBuilderReducer';
import {
  selectMacroControlsViewMode,
  selectPageNumber,
  selectSolveViewMode,
  selectTimeFrameFilter,
  selectTimeRange,
} from 'src/reducers/pageSettingsReducer/pageSettingsReducer';
import { CustomRangeModifier } from 'src/reducers/pageSettingsReducer/types';
import { selectChannel, selectChannels } from 'src/reducers/solveReducer';
import {
  selectCurrentTab,
  selectDashboardApplicationsByRole,
  selectIsSolveLiteEnabled,
  selectUser,
  selectUserRole,
} from 'src/reducers/userReducer/userReducer';
import { emitTrackingEventApi } from 'src/services/api';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api';
import { useGetOrgConfigQuery } from 'src/services/settings/settingsApi';
import { useGetPublishedWidgetConfigurationsQuery } from 'src/services/solve-config/solveConfigApi';
import {
  useGetIntentsQuery,
  useGetWorkflowTagsV2Query,
  useInitWorkflowBuilderSettingsQuery,
} from 'src/services/workflow-builder-metrics';
import { useAppDispatch } from 'src/store/hooks';
import { SolveWidgetProduct } from 'src/types/types';
import { ConversationChannel } from 'src/types/workflowBuilderAPITypes';
import { stripBase, withBase } from 'src/utils/basePath';
import { CHANNEL_TO_PRODUCT_MAP } from 'src/utils/constants';

const useConstructQueryParams = (): void => {
  const range: CustomRangeModifier = useSelector(selectTimeRange);
  const { from, to } = range;
  const timeframe = useSelector(selectTimeFrameFilter);
  const channel = useSelector(selectChannel);
  const page = useSelector(selectPageNumber);
  const view = useSelector(selectSolveViewMode);
  const startTime = from ? moment(from).unix() : 0;
  const endTime = to ? moment(to).unix() : 9999999999;
  const macroTableView = useSelector(selectMacroControlsViewMode);

  const pathName =
    stripBase(location.pathname) === '/'
      ? withBase('/solve-analytics')
      : window.location.pathname;

  useEffect(() => {
    if (view === 'breakdowns') {
      window.history.replaceState(
        null,
        '',
        `${pathName}?view=breakdowns${encodeURIComponent(
          `&start=${startTime}&end=${endTime}&channel=${channel}&page=${page}`,
        )}`,
      );
    }
    if (view === 'overview') {
      window.history.replaceState(
        null,
        '',
        `${pathName}?view=overview${encodeURIComponent(
          `&start=${startTime}&end=${endTime}&channel=${channel}&timeframe=${timeframe}`,
        )}`,
      );
    }
    if (view === 'Macro Controls') {
      window.history.replaceState(
        null,
        '',
        `${pathName}?view=macro controls${encodeURIComponent(
          `&status=${macroTableView} `,
        )}`,
      );
    }
  }, [
    range,
    timeframe,
    channel,
    page,
    view,
    macroTableView,
    pathName,
    startTime,
    endTime,
  ]);
};

const useLoadSolve = (): void => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('solve-analytics');
  useEffect((): void => {
    // @ts-expect-error Assume that the type is correct
    const queryStr: {
      channel: string;
      end: number;
      page: number;
      start: number;
      status: string;
      timeframe: string;
      view: string;
    } = queryString.parse(
      decodeURIComponent(location.search.replace(/\%20/g, '_')),
    );
    const { channel, end, page, start, status, timeframe, view } = queryStr;

    const isSolveAnalyticsView = view === 'overview' || view === 'breakdowns';

    const emptyChannel = !channel || channel?.toLowerCase() === 'undefined';
    dispatch(setIsSolveWorkflows(false));
    dispatch(
      setFilterType({ key: 'all', name: t('filters.allMacrosAndArticles') }),
    );
    if (
      Object.entries(queryStr).length === 0 ||
      (isSolveAnalyticsView && emptyChannel)
    ) {
      dispatch(setSolveView('overview'));
      dispatch(getSolveChannelsAPI());
    }
    if (isSolveAnalyticsView) {
      if (view === 'overview') {
        dispatch(setTimeFrameFilter(timeframe));
      }
      if (view === 'breakdowns') {
        dispatch(setPageNumber(page));
      }
      dispatch(setChannel(channel));
      dispatch(setSolveView(view));
      dispatch(
        setTimeRange({
          from: new Date(start * 1000),
          to: new Date(end * 1000),
        }),
      );
      dispatch(getSolveChannelsAPI());
    }
    if (view === 'macro_controls') {
      dispatch(setSolveView('Macro Controls'));
      dispatch(setMacroControlsViewMode(cleanStr(status).trim()));
      dispatch(getMacroControlsData());
    }
  }, [dispatch, t]);
};

const useSendInitialTrackingEvent = (): void => {
  const { t } = useTranslation('solve-analytics');
  const dispatch = useAppDispatch();
  const channels = useSelector(selectChannels);
  const channel = useSelector(selectChannel);
  const viewMode = useSelector(selectSolveViewMode);
  const timeRange = useSelector(selectTimeRange);
  const timeframeFilter = useSelector(selectTimeFrameFilter);
  const application = Permission.SOLVE;
  const eventType = 'initial_load';
  const data = useMemo(
    () => ({
      channel,
      view_mode: viewMode,
    }),
    [channel, viewMode],
  );

  useEffect(() => {
    if (channels.length) {
      sendTrackingEvents(
        dispatch,
        eventType,
        timeRange,
        application,
        viewMode === 'overview'
          ? { ...data, timeframe_filter: timeframeFilter }
          : { ...data, filter_type: getFilterDict(t)[0].name },
      );
    }
  }, [
    application,
    channels,
    data,
    dispatch,
    t,
    timeRange,
    timeframeFilter,
    viewMode,
  ]);
};

export const useSetDefaultTimeRangeQueryParams = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: featureFlagsResponse } = useGetFeatureFlagsQuery();
  const isInAssist = useIsInAssist();

  const locationHasQueryParams =
    location.search !== '?' && location.search.length > 0;

  useEffect(() => {
    if (!locationHasQueryParams && featureFlagsResponse) {
      const isSolveWidgetEnabled = featureFlagsResponse.feature_flags.includes(
        'solve_widget_enabled',
      );
      const getChannel = () => {
        if (isInAssist) {
          return 'playbook';
        }
        if (isSolveWidgetEnabled) {
          return 'widget';
        }
        return 'email';
      };
      const queryParams = constructTimeRangeQueryParamsRevamped(
        last30DaysTimeRange,
        getChannel(),
      );

      navigate(`?${queryParams}`);
    }
  }, [
    navigate,
    locationHasQueryParams,
    location,
    featureFlagsResponse,
    isInAssist,
  ]);
};

export const useLoadWorkflowBuilderFromURL = (): void => {
  const dispatch = useAppDispatch();
  const routerLocation = useLocation();

  const { isSuccess: isInitWorkflowBuilderSettingsSuccess } =
    useInitWorkflowBuilderSettingsQuery();

  const pathName = routerLocation.pathname || '';
  const isLandingPage = pathName === Routes.WORKFLOW_BUILDER;
  const isEditWorkflowsPage = pathName === Routes.WORKFLOW_BUILDER_EDIT;

  useEffect(() => {
    if (!isInitWorkflowBuilderSettingsSuccess) {
      return;
    }

    // @ts-expect-error Assume that the type is correct
    const queryStr: {
      channel: string;
      end: number;
      start: number;
      workflowId: string;
    } = queryString.parse(
      decodeURIComponent(location.search.replace(/\%20/g, '_')),
    );

    const getWorkflowBuilderData = async () => {
      await dispatch(getWorkflowBuilderLandingData());
    };

    const { channel, end, start } = queryStr;

    if (isLandingPage) {
      dispatch(
        setWorkflowBuilderChannel(
          channelsList.includes(channel) ? channel : 'widget',
        ),
      );
      if (start && end) {
        dispatch(
          setTimeRange({
            from: new Date(start * 1000),
            to: new Date(end * 1000),
          }),
        );
      }
      getWorkflowBuilderData();
    }
  }, [
    dispatch,
    isLandingPage,
    isEditWorkflowsPage,
    isInitWorkflowBuilderSettingsSuccess,
  ]);
};

export const useFlamethrowerTrackingEventAction = (
  application: FlamethrowerTrackingApplications,
): ((
  eventType: FlamethrowerTrackingEventTypes,
  params?: Record<string, string | number | string[]>,
) => void) => {
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const dispatch = useAppDispatch();
  const { auth_time: authTime, email } = user?.user ?? {};

  return useCallback(
    (
      eventType: FlamethrowerTrackingEventTypes,
      params?: Record<string, string | number | string[]>,
    ) => {
      dispatch(
        sendTrackingEvent(ModuleName.FLAMETHROWER, {
          application,
          event_type: eventType,
          session_id: authTime,
          user_email: email,
          user_role: userRole,
          ...params,
        }),
      );
    },
    [dispatch, application, authTime, userRole, email],
  );
};

export const useSolveConfigTrackingEventAction = (): ((
  eventType: SolveConfigTrackingEventTypes,
  params?: Record<string, string | number | string[]>,
) => void) => {
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const isSolveLiteEnabled = useSelector(selectIsSolveLiteEnabled);

  const dispatch = useAppDispatch();

  const { auth_time: authTime, email } = user?.user ?? {};
  const moduleName = isSolveLiteEnabled
    ? ModuleName.SOLVE_LITE
    : ModuleName.SOLVE_CONFIG;

  return useCallback(
    (
      eventType: SolveConfigTrackingEventTypes,
      params?: Record<string, string | number | string[]>,
    ) => {
      dispatch(
        sendTrackingEvent(moduleName, {
          event_type: eventType,
          session_id: authTime,
          user_email: email,
          user_role: userRole,
          ...params,
        }),
      );
    },
    [dispatch, authTime, userRole, email, moduleName],
  );
};

export const useTrackCanvasWorkflowActionEditedEvent = (): ((
  params?: Record<string, string | number | string[]>,
) => void) => {
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const actionId = useSelector(selectCustomizableActionId);
  const dispatch = useAppDispatch();
  const { auth_time: authTime, email } = user?.user ?? {};

  return useCallback(
    (params?: Record<string, string | number | string[]>) => {
      dispatch(
        sendTrackingEvent(ModuleName.FLAMETHROWER, {
          actionId: actionId,
          application: FlamethrowerTrackingApplications.WORKFLOW_BUILDER_CANVAS,
          event_type: FlamethrowerTrackingEventTypes.WORKFLOW_ACTION_EDITED,
          session_id: authTime,
          user_email: email,
          user_role: userRole,
          ...params,
        }),
      );
    },
    [dispatch, actionId, authTime, userRole, email],
  );
};

// Will hide the NPS survey when loaded async outside of workflow builder tabs
const useMutationObserver = () => {
  useEffect(() => {
    const targetNode = document.getElementsByTagName('body')[0];

    // Options for the observer (which mutations to observe)
    const config = { childList: true };

    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(mutationObserverCallback);

    // Start observing the target node for configured mutations
    targetNode && observer.observe(targetNode, config);

    return () => {
      observer.disconnect();
    };
  }, []);
};

const useHandleNPSSurveyVisibility = () => {
  const currentTab = useSelector(selectCurrentTab);

  useEffect(() => {
    const npsSurveyDiv = document.getElementById('npsIframeContainer');

    const workflowBuilerTabs = [
      Tabs.WORKFLOW_BUILDER,
      Tabs.WORKFLOW_BUILDER_EDIT,
    ];

    const shouldDisplayNpsSurvey =
      // @ts-expect-error legacy code with untyped state
      currentTab && workflowBuilerTabs.includes(currentTab);

    if (npsSurveyDiv) {
      npsSurveyDiv.style.display = shouldDisplayNpsSurvey ? 'block' : 'none';
    }
  }, [currentTab]);
};

const useNpsSurvey = () => {
  useMutationObserver();
  useHandleNPSSurveyVisibility();
};

const useListenPreviewPostMessageEvents = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    const handleMessage = (e: MessageEvent): void => {
      if (e.data.event === 'solveWidgetClosed') {
        dispatch(setIsOverlayVisible(false));
      }

      if (e.data.event === 'solveWidgetLoaded') {
        dispatch(setIsPreviewLoaded(true));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [dispatch]);
};

export interface TrackingEventAdditionalParams extends Record<string, unknown> {
  model_name?: string;
  scope?: TrackingEventScope;
}

const useEmitTrackingEventCallback = () => {
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const { auth_time: sessionId } = user?.user ?? {};

  return useCallback(
    (
      eventType: TrackingEventType,
      additionalParams?: TrackingEventAdditionalParams,
    ) => {
      emitTrackingEventApi({
        eventType,
        sessionId,
        userRole,
        ...additionalParams,
      });
    },
    [sessionId, userRole],
  );
};

const useEmitTrackingEvent = (
  eventType: TrackingEventType,
  additionalParams?: TrackingEventAdditionalParams,
) => {
  const emitTrackingEventCallback = useEmitTrackingEventCallback();
  const hasMounted = useRef(false);
  const { model_name, scope } = additionalParams ?? {};

  useEffect(() => {
    if (hasMounted.current) {
      return;
    }
    emitTrackingEventCallback(eventType, { model_name, scope });

    hasMounted.current = true;
  }, [eventType, emitTrackingEventCallback, model_name, scope]);
};

// Active useUrlParamBatch collector. Events and renders are synchronous, so a
// module-level slot is safe: it is only ever non-null for the duration of one
// batch callback's call stack.
let activeUrlParamBatch: URLSearchParams | null = null;

/**
 * Batches multiple useStateParams setter calls into a single navigation.
 *
 * Without batching, same-tick setter calls each rebuild the search string
 * from the location captured at the last render, so the final navigate
 * clobbers the earlier params in the URL. The legacy escape hatch is the
 * setters' second `batchUpdater` argument (a chain threading URLSearchParams
 * through nested callbacks); prefer this hook for new code — plain sequential
 * setter calls, one history entry:
 *
 *   const batchUrlParams = useUrlParamBatch();
 *   batchUrlParams(() => {
 *     setSortColumn(initialSort);
 *     setChannels([]);
 *   });
 *
 * The callback must be synchronous: setters called after it returns are not
 * part of the batch.
 */
const useUrlParamBatch = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (applyUpdates: () => void, options?: { replace?: boolean }) => {
      if (activeUrlParamBatch) {
        throw new Error('useUrlParamBatch: nested batches are not supported');
      }
      activeUrlParamBatch = new URLSearchParams(location.search);
      try {
        applyUpdates();
      } finally {
        const batchedParams = activeUrlParamBatch;
        activeUrlParamBatch = null;
        navigate(
          {
            pathname: location.pathname,
            search: batchedParams.toString(),
          },
          { replace: options?.replace ?? false },
        );
      }
    },
    [location.pathname, location.search, navigate],
  );
};

const useStateParams = <T>({
  deserialize,
  initialState,
  paramsName,
  replace = false,
  serialize,
  validator,
}: {
  deserialize: (serializedState: string) => T;
  initialState: T;
  paramsName: string;
  replace?: boolean;
  serialize: (state: T) => string;
  validator?: (param: string) => boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Use refs for callbacks to prevent re-renders when consumers pass inline functions
  const deserializeRef = useRef(deserialize);
  deserializeRef.current = deserialize;
  const serializeRef = useRef(serialize);
  serializeRef.current = serialize;
  const validatorRef = useRef(validator);
  validatorRef.current = validator;
  const initialStateRef = useRef(initialState);
  initialStateRef.current = initialState;

  const params = new URLSearchParams(location.search);
  const existingValue = params.get(paramsName);
  const parameterPassesValidation =
    existingValue && validatorRef.current
      ? validatorRef.current(existingValue)
      : true;
  const [state, setState] = useState<T>(
    existingValue && parameterPassesValidation
      ? deserializeRef.current(existingValue)
      : initialState,
  );

  const onChange = useCallback(
    (state: T, batchUpdater?: () => URLSearchParams) => {
      setState(state);

      // Inside a useUrlParamBatch callback: contribute to the batch's params;
      // the batch issues one navigation for all setters when it completes.
      if (activeUrlParamBatch) {
        activeUrlParamBatch.set(paramsName, serializeRef.current(state));
        return activeUrlParamBatch;
      }

      if (batchUpdater) {
        const prevParams = batchUpdater();
        prevParams.set(paramsName, serializeRef.current(state));
        navigate(
          {
            pathname: location.pathname,
            search: prevParams.toString(),
          },
          { replace: replace },
        );
        return prevParams;
      }

      const searchParams = new URLSearchParams(location.search);
      searchParams.set(paramsName, serializeRef.current(state));
      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: replace },
      );
      return searchParams;
    },
    [location.pathname, location.search, paramsName, navigate, replace],
  );

  // Use ref to avoid onChange in deps causing re-runs on every navigation
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Stable reference that delegates to the latest onChange via ref
  const stableOnChange = useCallback(
    (...args: Parameters<typeof onChange>) => onChangeRef.current(...args),
    [],
  );

  useEffect(() => {
    // Updates state when user navigates backwards or forwards in browser history
    if (existingValue !== null) {
      setState(oldState => {
        const deserializedExistingValue = deserializeRef.current(existingValue);
        if (
          deserializedExistingValue !== oldState &&
          parameterPassesValidation
        ) {
          return deserializedExistingValue;
        }
        if (!parameterPassesValidation) {
          onChangeRef.current(oldState);
        }
        return oldState;
      });
    } else {
      // The param was removed from the URL (e.g. a navigation that rebuilt
      // the search string, like switching workflow-builder channels): fall
      // back to initialState instead of keeping stale state. Only runs when
      // existingValue transitions to null, so optimistic local updates whose
      // navigation hasn't landed yet are not clobbered.
      setState(initialStateRef.current);
    }
  }, [existingValue, paramsName, parameterPassesValidation]);

  return [state, stableOnChange] as const;
};

const useGetSolveWidgetProduct = (): SolveWidgetProduct => {
  const [query] = useSearchParams();

  if (query.get('view')) {
    const view = query.get('view') as BuilderView;
    return CHANNEL_TO_PRODUCT_MAP[view === 'email' ? 'widget' : view];
  }
  if (query.get('channel')) {
    const channel = query.get('channel') as ConversationChannel;
    return CHANNEL_TO_PRODUCT_MAP[channel === 'email' ? 'widget' : channel];
  }
  return 'workflow_builder';
};

const useGetIntentsQueryWithProduct = (
  params?: {
    end_timestamp?: number | null;
    include_inquiry_counts?: boolean;
    start_timestamp?: number | null;
  },
  options?: {
    productOverride?: SolveWidgetProduct;
    refetchOnMountOrArgChange?: boolean;
    skip?: boolean;
  },
) => {
  const product = useGetSolveWidgetProduct();
  return useGetIntentsQuery(
    { ...params, product: options?.productOverride ?? product },
    options,
  );
};

const useGetAllIntentsQuery = ({ skip = false }: { skip?: boolean } = {}) => {
  const { data: orgConfigData } = useGetOrgConfigQuery();
  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const isSolveWidgetEnabled = featureFlags.includes('solve_widget_enabled');
  const isSolveEmailEnabled = featureFlags.includes('solve_email_enabled');
  const isSolveApiEnabled = featureFlags.includes('solve_api');
  const isSolveSlackEnabled = featureFlags.includes('solve_slack');
  const isSolveVoiceEnabled = featureFlags.includes('solve_voice');
  const isWebCallAsChannelEnabled = featureFlags.includes(
    'web_call_as_channel',
  );
  const isPlaybookEnabled = orgConfigData?.is_assist_gpt_enabled;

  const {
    data: solveWidgetIntentsResponse,
    isFetching: isSolveWidgetIntentsFetching,
  } = useGetIntentsQueryWithProduct(
    {},
    {
      productOverride: 'workflow_builder',
      skip: skip || !isSolveWidgetEnabled,
    },
  );
  const {
    data: solveEmailIntentsResponse,
    isFetching: isSolveEmailIntentsFetching,
  } = useGetIntentsQueryWithProduct(
    {},
    {
      productOverride: 'interactive_email',
      skip: skip || !isSolveEmailEnabled,
    },
  );
  const {
    data: solveApiIntentsResponse,
    isFetching: isSolveApiIntentsFetching,
  } = useGetIntentsQueryWithProduct(
    {},
    { productOverride: 'api', skip: skip || !isSolveApiEnabled },
  );
  const {
    data: solveSlackIntentsResponse,
    isFetching: isSolveSlackIntentsFetching,
  } = useGetIntentsQueryWithProduct(
    {},
    { productOverride: 'slack', skip: skip || !isSolveSlackEnabled },
  );
  const {
    data: solveVoiceIntentsResponse,
    isFetching: isSolveVoiceIntentsFetching,
  } = useGetIntentsQueryWithProduct(
    {},
    { productOverride: 'voice', skip: skip || !isSolveVoiceEnabled },
  );
  const {
    data: solvePlaybookIntentsResponse,
    isFetching: isSolvePlaybookIntentsFetching,
  } = useGetIntentsQueryWithProduct(
    {},
    { productOverride: 'playbook', skip: skip || !isPlaybookEnabled },
  );
  const {
    data: solveWebCallIntentsResponse,
    isFetching: isSolveWebCallIntentsFetching,
  } = useGetIntentsQueryWithProduct(
    {},
    { productOverride: 'web_call', skip: skip || !isWebCallAsChannelEnabled },
  );

  const intents = useMemo(
    () => [
      ...(solveWidgetIntentsResponse?.intents || []),
      ...(solveEmailIntentsResponse?.intents || []),
      ...(solveApiIntentsResponse?.intents || []),
      ...(solveSlackIntentsResponse?.intents || []),
      ...(solveVoiceIntentsResponse?.intents || []),
      ...(solvePlaybookIntentsResponse?.intents || []),
      ...(solveWebCallIntentsResponse?.intents || []),
    ],
    [
      solveApiIntentsResponse?.intents,
      solveEmailIntentsResponse?.intents,
      solveSlackIntentsResponse?.intents,
      solveVoiceIntentsResponse?.intents,
      solveWidgetIntentsResponse?.intents,
      solvePlaybookIntentsResponse?.intents,
      solveWebCallIntentsResponse?.intents,
    ],
  );

  return {
    data: intents,
    dataByProduct: {
      api: solveApiIntentsResponse?.intents || [],
      email: solveEmailIntentsResponse?.intents || [],
      playbook: solvePlaybookIntentsResponse?.intents || [],
      slack: solveSlackIntentsResponse?.intents || [],
      voice: solveVoiceIntentsResponse?.intents || [],
      web_call: solveWebCallIntentsResponse?.intents || [],
      widget: solveWidgetIntentsResponse?.intents || [],
    },
    isFetching:
      isSolveWidgetIntentsFetching ||
      isSolveEmailIntentsFetching ||
      isSolveApiIntentsFetching ||
      isSolveSlackIntentsFetching ||
      isSolveVoiceIntentsFetching ||
      isSolvePlaybookIntentsFetching ||
      isSolveWebCallIntentsFetching,
  };
};

export const useIsFeatureFlagEnabled = (featureFlag: string): boolean => {
  const { data: featureFlagsResponse } = useGetFeatureFlagsQuery();
  return Boolean(featureFlagsResponse?.feature_flags.includes(featureFlag));
};

export const useHasPermissionToApplication = (
  application: Permission,
): boolean => {
  // Given an application, check if the user has permission to access it
  const dashboardApplications = useSelector(selectDashboardApplicationsByRole);
  return Boolean(
    application in dashboardApplications && dashboardApplications[application],
  );
};

export const useHasAiQaAccess = (): boolean => {
  return useHasPermissionToApplication(Permission.AI_QA);
};

const useGetBrandColor = (index: number) => {
  const { palette } = useTheme();
  const colors = [
    palette.colors.blue,
    palette.colors.green,
    palette.colors.purple,
    palette.colors.red,
    palette.colors.yellow,
  ];
  // Guard against negative, non-integer, or NaN indices (e.g. a `-1` sentinel
  // from a missed lookup): JS modulo keeps the sign, so `-1 % length` is `-1`,
  // which would index past the array and crash. Normalize to a valid slot.
  const safeIndex = Number.isInteger(index) && index >= 0 ? index : 0;
  const indexInColorsArray = safeIndex % colors.length;

  return colors[indexInColorsArray][300];
};

const useGetWorkflowTags = () => {
  const { data: workflowTagsV2 = [] } = useGetWorkflowTagsV2Query();

  return workflowTagsV2;
};

const useIsWorkflowUsedByWidgetConfiguration = () => {
  const { data } = useGetPublishedWidgetConfigurationsQuery();
  const publishedWidgetConfigs = data?.solve_widget_configurations;

  const quickFeedbackWorkflowIds = useMemo(() => {
    const result: string[] = [];

    if (!publishedWidgetConfigs) {
      return result;
    }

    publishedWidgetConfigs.forEach(widgetConfig => {
      [
        widgetConfig.quick_feedback_config.negative_routing_intent_workflow_id,
        widgetConfig.quick_feedback_config.positive_routing_intent_workflow_id,
        widgetConfig.csat_config.negative_rating_routing_intent,
      ].forEach(id => id && result.push(id));
    });

    return result;
  }, [publishedWidgetConfigs]);

  const sentimentAnalysisWorkflowIds = useMemo(() => {
    const result: string[] = [];

    if (!publishedWidgetConfigs) {
      return result;
    }

    publishedWidgetConfigs.forEach(widgetConfig => {
      [
        widgetConfig.sentiment_analysis_config
          .negative_sentiment_routing_intent_workflow_id,
      ]
        .filter(Boolean)
        .forEach(id => id && result.push(id));
    });

    return result;
  }, [publishedWidgetConfigs]);

  const errorHandlingWorkflowIds = useMemo(() => {
    const result: string[] = [];

    if (!publishedWidgetConfigs) {
      return result;
    }

    publishedWidgetConfigs.forEach(widgetConfig => {
      // Older published configs may predate error_handling_config.
      const errorHandlingConfig = widgetConfig.error_handling_config;
      if (
        errorHandlingConfig?.is_enabled &&
        errorHandlingConfig.fallback_intent_workflow_id
      ) {
        result.push(errorHandlingConfig.fallback_intent_workflow_id);
      }
    });

    return result;
  }, [publishedWidgetConfigs]);

  const isWorkflowIdInUse = useCallback(
    (intentWorkflowId: string | null) => {
      if (!intentWorkflowId) {
        return false;
      }
      return [
        ...quickFeedbackWorkflowIds,
        ...sentimentAnalysisWorkflowIds,
        ...errorHandlingWorkflowIds,
      ].includes(intentWorkflowId);
    },
    [
      quickFeedbackWorkflowIds,
      sentimentAnalysisWorkflowIds,
      errorHandlingWorkflowIds,
    ],
  );

  return isWorkflowIdInUse;
};

// TODO expand to other products by enablement
const useProductForWorkflowId = (
  intentWorkflowId: string | null | undefined,
  emailComponentId?: string | null | undefined,
): SolveWidgetProduct | undefined => {
  const { data: orgConfigData } = useGetOrgConfigQuery();
  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const isSolveSlackEnabled = featureFlags.includes('solve_slack');
  const isSolveEmailEnabled = featureFlags.includes('solve_email_enabled');
  const isPlaybookEnabled = orgConfigData?.is_assist_gpt_enabled;

  const { data: widgetData } = useGetIntentsQueryWithProduct(undefined, {
    productOverride: 'workflow_builder',
  });

  const { data: apiData } = useGetIntentsQueryWithProduct(undefined, {
    productOverride: 'api',
  });

  const { data: assistData } = useGetIntentsQueryWithProduct(undefined, {
    productOverride: 'playbook',
    skip: !isPlaybookEnabled,
  });

  const { data: slackData } = useGetIntentsQueryWithProduct(undefined, {
    productOverride: 'slack',
    skip: !isSolveSlackEnabled,
  });

  const { data: emailData } = useGetIntentsQueryWithProduct(undefined, {
    productOverride: 'interactive_email',
    skip: !isSolveEmailEnabled,
  });

  return useMemo(() => {
    if (emailComponentId) {
      return 'interactive_email';
    }

    if (!intentWorkflowId) {
      return undefined;
    }

    const isWidget = widgetData?.intents.find(
      intent => intent.intent_workflow_id === intentWorkflowId,
    );

    const isApi = apiData?.intents.find(
      intent => intent.intent_workflow_id === intentWorkflowId,
    );

    const isPlaybook =
      isPlaybookEnabled &&
      assistData?.intents.find(
        intent => intent.intent_workflow_id === intentWorkflowId,
      );

    const isSlack =
      isSolveSlackEnabled &&
      slackData?.intents.find(
        intent => intent.intent_workflow_id === intentWorkflowId,
      );

    const isEmail =
      isSolveEmailEnabled &&
      emailData?.intents.find(
        intent => intent.intent_workflow_id === intentWorkflowId,
      );

    if (isWidget) {
      return 'workflow_builder';
    }

    if (isApi) {
      return 'api';
    }

    if (isPlaybook) {
      return 'playbook';
    }

    if (isSlack) {
      return 'slack';
    }

    if (isEmail) {
      return 'interactive_email';
    }

    return undefined;
  }, [
    widgetData,
    apiData,
    assistData,
    slackData,
    emailData,
    intentWorkflowId,
    emailComponentId,
    isPlaybookEnabled,
    isSolveSlackEnabled,
    isSolveEmailEnabled,
  ]);
};

export {
  useConstructQueryParams,
  useEmitTrackingEvent,
  useEmitTrackingEventCallback,
  useGetAllIntentsQuery,
  useGetBrandColor,
  useGetIntentsQueryWithProduct,
  useGetSolveWidgetProduct,
  useGetWorkflowTags,
  useIsInAssist,
  useIsWorkflowUsedByWidgetConfiguration,
  useListenPreviewPostMessageEvents,
  useLoadSolve,
  useNpsSurvey,
  useProductForWorkflowId,
  useSendInitialTrackingEvent,
  useStateParams,
  useUrlParamBatch,
};
